import cloneDeep from "lodash/cloneDeep";
import { GenerateDataV2 } from "./ProblemGenerator.tsx";
import { applyRouteSettings, settingsForRoute } from "./RouteSettings.ts";
import { DefaultSettings } from "./Models.ts";
import type { ISettingsProps } from "./Models.ts";
import { Operations, ProblemData, ProblemFormatOptions } from "./Constants.ts";
import { factFinderLevels, factFinderPage } from "./FactFinder.ts";
import { ladderFor, ladderTitle } from "./FactLevels.ts";
import { gradeName } from "./SubPack.ts";
import { findBySlug } from "../Shared/Seo/routeSeo.ts";
import type { PackActivity, PackSpec } from "../Shared/Seo/routeSeo.ts";
import type { SheetSpec, WhereToStartRow } from "./PdfGenerator/sheetSpec.ts";
import { withSeed } from "../Shared/Helpers.tsx";

/**
 * A placement check (ADR 0086) as a list of sheets: a cover for the teacher, two sheets of strands,
 * a page of four stories, a fact finder, the keys the sheets bring with them, and "Where to start".
 * `Pages` already puts every worksheet page of a multi-document set before every key, so the order
 * falls out: cover, four sheets, four keys, and the last page, which goes in with the keys.
 *
 * **Each strand's two items are drawn from the page the key sends you to.** A strand is an activity
 * of the pack - an existing page of the site, with any settings the pack overrides - and its items are
 * what that page's own generator makes with the count set to two. So "every item belongs to the page it
 * names" is true by construction, and stays true as those generators change.
 *
 * A sheet of strands is built the way a spiral review's page is - problems tagged with the heading
 * they sit under, drawn by each format's own renderer - but each strand is generated at its own
 * settings, which a spiral review cannot do (it shares one set of settings across its sections), and
 * headed with the strand's name, which is what "Where to start" keys on. The sheet carries its problems
 * as pre-built page data, as the missed-facts practice sheet does (ADR 0012 §3).
 *
 * Seeds follow the pack pattern (ADR 0083 §5): the check's ID is the one on the cover, student sheet
 * `i` prints `${seed}-${i}`, and each draw is made inside `withSeed` so the check rebuilds from `seed`.
 */

const ITEMS_PER_STRAND = 2;
const STORIES = 4;

/** A strand's page, at the settings the pack gives it, pinned to the two items the check asks. */
function strandSettings(activity: PackActivity, count: number): ISettingsProps {
    const base = findBySlug(activity.slug);
    if (!base) {
        throw new Error(`Placement check: no page at ${activity.slug}`);
    }
    const settings = applyRouteSettings(settingsForRoute(base), activity.settings ?? {});
    settings.NumberOfProblemsPerPage = count;
    settings.NumberOfPages = 1;
    settings.HalfPage = false;
    settings.AnswerPages = false;
    settings.Timed = false;
    settings.DifferentiationSet = false;
    // A fact-fluency page opens on a level; a strand is a few of that page's problems, not a level test.
    settings.FactLevel = 0;
    return settings;
}

function strandProblems(activity: PackActivity, seed: string): ProblemData[] {
    const settings = strandSettings(activity, ITEMS_PER_STRAND);
    const heading = activity.name ?? activity.slug;
    const pages = withSeed(seed, () => GenerateDataV2(settings));
    return (pages?.[0] ?? []).map((problem) => ({
        ...problem,
        problemGroup: { key: heading, heading, format: settings.Format },
    }));
}

/** A sheet that is only its pre-built page data. `Format` picks the renderer, nothing else reads it. */
function dataSheetSettings(format: ProblemFormatOptions, problems: number): ISettingsProps {
    const settings = cloneDeep(DefaultSettings);
    settings.Format = format;
    settings.NumberOfPages = 1;
    settings.NumberOfProblemsPerPage = problems;
    settings.HalfPage = false;
    settings.AnswerPages = true;
    settings.Timed = false;
    settings.DifferentiationSet = false;
    settings.FactLevel = 0;
    return settings;
}

const address = (slug: string) => `math-pages.com${slug}`;

export function placementSheets(pack: PackSpec, seed: string): SheetSpec[] {
    const check = pack.check;
    if (!check) {
        throw new Error("Placement check: the pack has no `check`");
    }
    const strandRows: WhereToStartRow[] = [];
    const sheets: SheetSpec[] = [];

    // Student sheet 0 and 1: the strands, two items each, under their own headings.
    check.sheets.forEach((indexes, sheetIndex) => {
        const problems = indexes.flatMap((index) => {
            const activity = pack.activities[index];
            strandRows.push({ name: activity.name ?? activity.slug, address: address(activity.slug) });
            return strandProblems(activity, `${seed}:s${index}`);
        });
        sheets.push({
            settings: dataSheetSettings(ProblemFormatOptions.SpiralReview, problems.length),
            seed: `${seed}:${sheetIndex}`,
            footerSeed: `${seed}-${sheetIndex}`,
            pageData: [problems],
        });
    });

    // Student sheet 2: four stories, drawn by the page they come from, so the Varied rotation and the
    // exact split of operations are that page's own.
    const storiesActivity = pack.activities[check.stories];
    const storiesSettings = strandSettings(storiesActivity, STORIES);
    storiesSettings.AnswerPages = true;
    const storiesIndex = sheets.length;
    sheets.push({ settings: storiesSettings, seed: `${seed}:${storiesIndex}`, footerSeed: `${seed}-${storiesIndex}` });

    // Student sheet 3: the fact finder, one fact from each level of the ladder, numbered.
    const finder = check.factFinder;
    const ladder = ladderFor(finder.operations as Operations[], 10);
    if (!ladder) {
        throw new Error(`Placement check: no fact ladder for ${finder.operations.join(", ")}`);
    }
    const finderIndex = sheets.length;
    const finderPage = withSeed(`${seed}:${finderIndex}`, () => factFinderPage(ladder));
    sheets.push({
        settings: dataSheetSettings(ProblemFormatOptions.NumberProperties, finderPage.length),
        seed: `${seed}:${finderIndex}`,
        footerSeed: `${seed}-${finderIndex}`,
        pageData: [finderPage],
    });

    const students = sheets.length;
    const strands = check.sheets.flat().length;
    const cover: SheetSpec = {
        // Not read: a cover draws no problems.
        settings: sheets[0].settings,
        seed,
        footerSeed: seed,
        placementCover: {
            grade: gradeName(pack.grade),
            reviews: pack.reviews,
            id: seed,
            strands,
            studentPages: `2 to ${students + 1}`,
            answerPages: `${students + 2} to ${2 * students + 1}`,
            wherePage: String(2 * students + 2),
        },
    };

    const finderActivity = pack.activities[finder.activity];
    const last: SheetSpec = {
        settings: sheets[0].settings,
        seed,
        footerSeed: seed,
        whereToStart: {
            id: seed,
            rows: strandRows,
            stories: { name: storiesActivity.name ?? "Stories", address: address(storiesActivity.slug) },
            finder: {
                ladder: ladderTitle(ladder.id),
                address: address(finderActivity.slug),
                levels: factFinderLevels(ladder, ladder.id),
            },
        },
    };

    return [cover, ...sheets, last];
}
